import { apiService } from '../lib/api';

export interface TimelineEvent {
  year: string;
  event: string;
}

export interface Personality {
  name: string;
  contributions: string;
}

export interface Subdomain {
  name: string;
  explaination: string;
}

export interface Specialisation {
  name: string;
  applications: string;
}

export interface SubjectMeta {
  icon: any;
  color: string;
  category: string;
  title: string;
}

export interface SparkSummary {
  _id: string;
  subject_name: string;
  introduction: string;
}

export interface SparkDetail {
  _id: string;
  subject_name: string;
  file_name: string;
  introduction: string;
  purpose: string;
  importance: string;
  utility: string;
  past: string;
  timeline: Array<{
    year: string;
    event: string;
  }>;
  storytelling?: string;
  world_impact?: string;
  personalities?: Array<{
    name: string;
    contributions: string;
  }>;
  subdomains?: Array<{
    name: string;
    explaination: string;
  }>;
  specialisations?: Array<{
    name: string;
    applications: string;
  }>;
  metadata: {
    source_file: string;
    processed_at: string;
    version: string;
  };
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export interface SparksListResponse {
  success: boolean;
  data: SparkSummary[];
  count: number;
}

export interface SparkDetailResponse {
  success: boolean;
  data: SparkDetail;
}

class SparksService {
  /**
   * Get all sparks (summary list)
   */
  async getSparks(): Promise<SparksListResponse> {
    try {
      const response = await apiService.get<SparksListResponse>('/sparks');
      return response;
    } catch (error) {
      console.error('Error fetching sparks:', error);
      throw error;
    }
  }

  /**
   * Get detailed spark data by ID
   */
  async getSparkById(id: string): Promise<SparkDetailResponse> {
    try {
      const response = await apiService.get<SparkDetailResponse>(`/sparks/${id}`);
      return response;
    } catch (error) {
      console.error(`Error fetching spark ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get spark data by subject name
   */
  async getSparkBySubjectName(subjectName: string): Promise<SparkDetailResponse> {
    try {
      const response = await apiService.get<SparkDetailResponse>(`/sparks/subject/${subjectName}`);
      return response;
    } catch (error) {
      console.error(`Error fetching spark for subject ${subjectName}:`, error);
      throw error;
    }
  }

  /**
   * Convert subject name to key format (for compatibility with existing code)
   */
  subjectNameToKey(subjectName: string): string {
    return subjectName.toLowerCase().replace(/\s+/g, '_');
  }

  /**
   * Convert key format back to subject name
   */
  keyToSubjectName(key: string): string {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}

export const sparksService = new SparksService();

// Subject metadata for UI styling and categorization
export const subjectMeta: Record<string, SubjectMeta> = {
  mathematics: {
    icon: 'Calculator',
    color: 'from-blue-500 to-indigo-600',
    category: 'STEM',
    title: 'Mathematics'
  },
  physics: {
    icon: 'Atom',
    color: 'from-purple-500 to-pink-600',
    category: 'STEM',
    title: 'Physics'
  },
  biology: {
    icon: 'Microscope',
    color: 'from-green-500 to-emerald-600',
    category: 'STEM',
    title: 'Biology'
  },
  chemistry: {
    icon: 'Atom',
    color: 'from-orange-500 to-red-600',
    category: 'STEM',
    title: 'Chemistry'
  },
  english: {
    icon: 'Book',
    color: 'from-teal-500 to-cyan-600',
    category: 'Languages',
    title: 'English'
  },
  history: {
    icon: 'Clock',
    color: 'from-amber-500 to-orange-600',
    category: 'Social Studies',
    title: 'History'
  },
  geography: {
    icon: 'Globe',
    color: 'from-cyan-500 to-blue-600',
    category: 'Social Studies',
    title: 'Geography'
  },
  social_science: {
    icon: 'Users',
    color: 'from-rose-500 to-pink-600',
    category: 'Social Studies',
    title: 'Social Science'
  },
  democratic_politics: {
    icon: 'Shield',
    color: 'from-indigo-500 to-purple-600',
    category: 'Social Studies',
    title: 'Democratic Politics'
  },
  disaster_management: {
    icon: 'Shield',
    color: 'from-red-500 to-rose-600',
    category: 'Applied Sciences',
    title: 'Disaster Management'
  }
};
